import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Card, CardBody, CardHeader, Col, Container, Row, Spinner, Label, Input, Button } from 'reactstrap'
import TableContainer from '../../Components/Common/TableContainerReactTable';
import DteRecibidosHooks from '../../Components/Hooks/DteRecibidosHooks';
import * as XLSX from 'xlsx';

const DteRecibidos = () => {

    const empresa = useSelector((state) => state.Context.empresa);
    const [fechaDesde, setFechaDesde] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    });
    const [fechaHasta, setFechaHasta] = useState(() => {
        const now = new Date();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return lastDay.toISOString().split('T')[0];
    });
    const [tipoDTE, setTipoDTE] = useState('33');
    const [acuseReciboGlosa, setAcuseReciboGlosa] = useState('')
    const [rutEmisor, setRutEmisor] = useState('')

    const { dtes, loading, error, fetchDtes } = DteRecibidosHooks();

    const formatDate = (date) => {
        if (!date) return '';
        const _date = new Date(date)
        const year = _date.getFullYear();
        const month = String(_date.getMonth() + 1).padStart(2, '0');
        const day = String(_date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatCurrency = (value) => {
        return value ? parseFloat(value).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' }) : '';
    };

    const exportToExcel = (filteredData) => {
        const formattedData = filteredData.map(item => ({
            "Folio": item.folio,
            "Tipo DTE": item.tipoDTE,
            "RUT Emisor": item.rutEmisor,
            "Razón Social Emisor": item.razonSocialEmisor,
            "Fecha Emisión": formatDate(item.fechaEmision),
            "Fecha Recepción": formatDate(item.fechaRecepcion),
            "Monto Neto": formatCurrency(item.montoNeto),
            "Monto IVA": formatCurrency(item.montoIVA),
            "Monto Total": formatCurrency(item.montoTotal),
            "A/R Fecha": item.fechaHoraAcuseRecibo,
            "A/R Estado": item.acuseReciboEstado,
            "A/R Glosa": item.acuseReciboGlosa
        }));

        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "DTE Recibidos");
        XLSX.writeFile(wb, "DTE_Recibidos.xlsx");
    };

    useEffect(() => {
        document.title = "DTE Recibidos | Siga ERP";
    }, []);

    useEffect(() => {
        fetchDtes(fechaDesde, fechaHasta, tipoDTE);
    }, [fechaDesde, fechaHasta, empresa, tipoDTE]);

    const columns = useMemo(
        () => [
            {
                header: "Folio",
                accessorKey: "folio",
                enableColumnFilter: false,
            },
            {
                header: "Tipo DTE",
                accessorKey: "tipoDTE",
                enableColumnFilter: false,
            },
            {
                header: "RUT Emisor",
                accessorKey: "rutEmisor",
                enableColumnFilter: false,
            },
            {
                header: "Razón Social Emisor",
                accessorKey: "razonSocialEmisor",
                enableColumnFilter: false,
            },
            {
                header: "Fecha Emisión",
                accessorKey: "fechaEmision",
                enableColumnFilter: false,
                cell: ({ getValue }) => formatDate(getValue()),
            },
            {
                header: "Fecha Recepción",
                accessorKey: "fechaRecepcion",
                enableColumnFilter: false,
                cell: ({ getValue }) => formatDate(getValue()),
            },
            {
                header: "Monto Neto",
                accessorKey: "montoNeto",
                enableColumnFilter: false,
                cell: ({ getValue }) => formatCurrency(getValue()),
            },
            {
                header: "Monto IVA",
                accessorKey: "montoIVA",
                enableColumnFilter: false,
                cell: ({ getValue }) => formatCurrency(getValue()),
            },
            {
                header: "Monto Total",
                accessorKey: "montoTotal",
                enableColumnFilter: false,
                cell: ({ getValue }) => formatCurrency(getValue()),
            },
            {
                header: "A/R Fecha",
                accessorKey: "fechaHoraAcuseRecibo",
                enableColumnFilter: false,
            },
            {
                header: "A/R Estado",
                accessorKey: "acuseReciboEstado",
                enableColumnFilter: false,
            },
            {
                header: "A/R Glosa",
                accessorKey: "acuseReciboGlosa",
                enableColumnFilter: false,
            },

        ],
        []
    );

    const handleCellClick = (cellInfo) => {
        console.log('Celda clickeada:', cellInfo);
        console.log('Columna:', cellInfo.columnId);
        console.log('Valor:', cellInfo.value);
        console.log('Datos completos de la fila:', cellInfo.rowData);
        switch (cellInfo.columnId) {
            case 'rutEmisor':
                setRutEmisor(cellInfo.value)
                break
            case 'acuseReciboGlosa':
                setAcuseReciboGlosa(cellInfo.value)
                break
            case 'tipoDTE':
                setTipoDTE(cellInfo.value)
                break
            default:
                break
        }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader className="d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0">DTE Recibidos</h5>
                                </CardHeader>
                                <CardBody>
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <Label for="fechaDesde">Fecha Desde</Label>
                                            <Input
                                                type="date"
                                                id="fechaDesde"
                                                value={fechaDesde}
                                                onChange={(e) => setFechaDesde(e.target.value)}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Label for="fechaHasta">Fecha Hasta</Label>
                                            <Input
                                                type="date"
                                                id="fechaHasta"
                                                value={fechaHasta}
                                                onChange={(e) => setFechaHasta(e.target.value)}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Label for="tipoDTE">Tipo DTE</Label>
                                            <Input
                                                type="text"
                                                id="tipoDTE"
                                                value={tipoDTE}
                                                onChange={(e) => setTipoDTE(e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col md={3}>
                                            <Label for="rutEmisor">Rut emisor</Label>
                                            <Input
                                                type="text"
                                                id="rutEmisor"
                                                value={rutEmisor}
                                                onChange={(e) => setRutEmisor(e.target.value)}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Label for="acuseReciboGlosa">Acuse recibo</Label>
                                            <Input
                                                type="text"
                                                id="acuseReciboGlosa"
                                                value={acuseReciboGlosa}
                                                onChange={(e) => setAcuseReciboGlosa(e.target.value)}
                                            />
                                        </Col>
                                    </Row>
                                    {error && <div className="alert alert-danger">{error}</div>}
                                    {loading ? <div className="text-center"><Spinner animation="border" variant="primary" /></div> : <TableContainer
                                        columns={(columns || [])}
                                        data={dtes}
                                        customPageSize={15}
                                        isGlobalFilter={true}
                                        SearchPlaceholder='Buscar...'
                                        onCellClick={handleCellClick}
                                        onExport={exportToExcel}
                                    />}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default DteRecibidos;